import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { trimLeadingZeros } from '@shared-helpers/string-number-helpers';

import { IAnyCatsType, ITableConfigEntity } from '../../../interfaces/internal-interfaces';
import { BillingTimesheet } from '../../../scripts/BillingTimesheet';
import { ExtDocument } from '../../../scripts/extDocument';
import { ProductionGroup } from '../../../scripts/Production-Group/ProductionGroup';
import { ProductionTextRecord } from '../../../scripts/ProductionTextRecord';
import { ModalService } from '../../../services/modal.service';
import { TimesheetHelperService } from '../../../services/timesheet-helper.service';

interface ITimesheetForSelection {
  description: string;
  headers: Array<{ name: string; val: string }>;
  extDoc: ExtDocument; //we keep the entire document here so we can grab it when a table selection is made, we don't actually use this in the table layout
}

@Component({
  selector: 'app-modal-content-timesheet-move',
  templateUrl: './modal-content-timesheet-move.component.html',
  standalone: false,
})
export class ModalContentTimesheetMoveComponent implements OnInit {
  @Output() public readonly closed = new EventEmitter<ExtDocument>();
  @Input() public record?: IAnyCatsType;
  @Input() public productionGroup?: ProductionGroup;
  @Input() public timesheet!: BillingTimesheet;

  public activityName = '';
  public otherTimesheets: ITimesheetForSelection[] = [];
  public otherTimesheetsTableConfig: ITableConfigEntity[] = [];

  public get isQuantity(): boolean {
    return this.record && (this.record.__type === 'pqt' || this.record.__type === 'peq');
  }

  public get isText(): boolean {
    return this.record && this.record.__type === 'ptx';
  }

  public get textDisplayName(): string {
    //This is only called in the the template after we have determined this is a ProductionText record type, so this is safe to force the type here
    return this.record ? (this.record as ProductionTextRecord).parent.display : '';
  }

  public get textDisplayDay(): string {
    //This is only called in the the template after we have determined this is a ProductionText record type, so this is safe to force the type here
    return this.record ? (this.record as ProductionTextRecord).parent.focusedDay.moment.format('dddd') : '';
  }

  constructor(
    private readonly timesheetHelperSvc: TimesheetHelperService,
    private readonly modalSvc: ModalService,
  ) {}

  public ngOnInit(): void {
    if (!this.record && !this.productionGroup) throw Error('You must provide either a record or a productionGroup!');
    if (this.record && this.productionGroup) {
      throw Error('You cannot provide BOTH a record AND a productionGroup, it must be one or the other!');
    }

    this.otherTimesheets = this.getOtherTimesheetsForSelection();
    if (this.record) this.activityName = this.timesheetHelperSvc.getActivityDescription(this.record);
  }

  public trimLeadingZeros(documentNo: string): string {
    return trimLeadingZeros(documentNo);
  }

  public handleMoveToExistingTimesheetClicked(extDoc: ExtDocument): void {
    this.closed.emit(extDoc);
  }

  public handleMoveToNewTimesheetClicked(): void {
    //Change the currently open modal to show different content and data.
    //When selected the 'closed' emitter will emit back to the original component that launched THIS modal!
    this.modalSvc.changeCurrentlyOpenModalComponent('timesheet-create-or-edit', {
      record: this.record, //OK to be undefined, the create component handles this
      productionGroup: this.productionGroup, //OK to be undefined, the create component handles this
      allExtDocuments: this.timesheet.extDocuments,
      applicableExtDocuments: this.timesheet.applicableExtDocuments,
      selectedCostCenterCondGrpTabs: { costCenter: this.timesheet.contractCostCenter, custGroup: this.timesheet.contractCustGrp },
    });
  }

  private getOtherTimesheetsForSelection(): ITimesheetForSelection[] {
    const currentDocNumber = this.record?.Extdocumentno ?? this.productionGroup.ExtDocumentno;

    //manipulate the data to only show what we need
    return this.timesheet.extDocuments
      .filter((doc) => doc.Documentno !== currentDocNumber)
      .map((doc) => {
        const tsInfo: ITimesheetForSelection = {
          description: doc.Description,
          headers: doc.headerConfig.map((h) => {
            const val = doc[h.key];
            return {
              name: h.header,
              val: typeof val === 'string' ? val.trim() : '',
            };
          }),

          extDoc: doc,
        };
        return tsInfo;
      });
  }
}
